import { useCallback, useState } from 'react';

import { graphql, useFragment, useMutation } from 'react-relay';

import type {
	CustomizationItem,
	NetworkStorageParams,
	SideNavData,
	UseSideNavUserData,
} from '../../../../common/types';

import { type useNetworkSideNavUserData$key } from './__generated__/useNetworkSideNavUserData.graphql';
import type {
	useNetworkSideNavUserDataMutation as CustomisationMutationType,
	useNetworkSideNavUserDataMutation$variables,
} from './__generated__/useNetworkSideNavUserDataMutation.graphql';

const DEFAULT_COLLAPSED_KEY = 'defaultCollapsed' as const;
const DEFAULT_WIDTH_KEY = 'defaultWidth' as const;

/**
 * useNetworkSideNavUserData - returns a tuple of user-data and a setter function to update the user customization data.
 * This can be used by the CustomizationModal to update the sidebar customization data.
 *
 * @experimental
 * Still under development. Use with caution.
 */
export function useNetworkSideNavUserData(params: NetworkStorageParams): UseSideNavUserData {
	const { entityAri, ownerAri, queryRef } = params;
	const queryData = useLocalFragment(queryRef);
	const mutation = useLocalMutation();
	// We store the state of the properties & customizationItems, as the sidebar state can change without any
	// new calls to the local queryRef (to read the value back). So we persist it in this component.
	const [sideNavProperties, setSideNavProperties] = useState({
		defaultWidth:
			(
				queryData?.userSideNavSettings?.properties?.nodes ?? [
					{ key: DEFAULT_WIDTH_KEY, value: undefined },
				]
			).find((item) => item?.key === DEFAULT_WIDTH_KEY)?.value ?? '',
		defaultCollapsed:
			(
				queryData?.userSideNavSettings?.properties?.nodes ?? [
					{ key: DEFAULT_COLLAPSED_KEY, value: undefined },
				]
			).find((item) => item?.key === DEFAULT_COLLAPSED_KEY)?.value ?? '',
	});
	const [customizationItems, setCustomizationItems] = useState(
		repairCustomizationItems(queryData?.userSideNavSettings?.sidebar?.nodes || []),
	);

	// Create two callback functions - one for each user data-prop.
	// Not sure how performant this is, but it simplifies updates (because the caller doesn't need to know about the other half of the data)
	const setUserMenuData = useCallback(
		async (userMenuData: CustomizationItem[]) => {
			const sidebar = userMenuData;
			setCustomizationItems(sidebar);
			const properties = [
				{ key: DEFAULT_WIDTH_KEY, value: sideNavProperties.defaultWidth },
				{ key: DEFAULT_COLLAPSED_KEY, value: sideNavProperties.defaultCollapsed },
			];

			return executeMutation({ mutation, variables: { entityAri, ownerAri, properties, sidebar } });
		},
		[entityAri, mutation, ownerAri, sideNavProperties],
	);

	const setUserSideNavData = useCallback(
		async (userSideNavData: SideNavData) => {
			const sidebar = customizationItems;
			const newState = {
				defaultWidth: String(userSideNavData.defaultWidth ?? sideNavProperties.defaultWidth) || '',
				defaultCollapsed:
					String(userSideNavData.defaultCollapsed ?? sideNavProperties.defaultCollapsed) || 'false',
			};
			setSideNavProperties(newState);
			const properties = [
				{ key: DEFAULT_WIDTH_KEY, value: newState.defaultWidth },
				{ key: DEFAULT_COLLAPSED_KEY, value: newState.defaultCollapsed },
			];

			return executeMutation({ mutation, variables: { entityAri, ownerAri, properties, sidebar } });
		},
		[entityAri, mutation, ownerAri, customizationItems, sideNavProperties],
	);

	// Create a nicer shape for the data.
	// For the userSideNavData, it is ok for the values to be undefined,
	// as the ADS component will know what to do.
	const niceUserData = {
		userMenuData: customizationItems,
		userSideNavData: {
			defaultWidth:
				sideNavProperties.defaultWidth === '' ? undefined : Number(sideNavProperties.defaultWidth),
			defaultCollapsed:
				sideNavProperties.defaultCollapsed === ''
					? undefined
					: sideNavProperties.defaultCollapsed === 'true',
		},
	};

	return { ...niceUserData, setUserMenuData, setUserSideNavData };
}

/**
 * Make the Relay data look "normal" to the outside world.
 */
function repairCustomizationItems(value: any): CustomizationItem[] {
	return value satisfies CustomizationItem[];
}

function useLocalFragment(queryRef: NetworkStorageParams['queryRef']) {
	/* eslint-disable @atlassian/relay/no-wrapping-hooks */
	/* eslint-disable @atlassian/relay/unused-fields */
	/* eslint-disable @atlassian/relay/graphql-naming */
	return useFragment<useNetworkSideNavUserData$key>(
		graphql`
			fragment useNetworkSideNavUserData on Query {
				userSideNavSettings: settings_navigationCustomisation(
					entityAri: $entityAriUserSideNavSettings
					ownerAri: $ownerAri
				) {
					sidebar {
						nodes {
							menuId
							visible
						}
					}
					properties {
						nodes {
							key
							value
						}
					}
				}
			}
		`,
		queryRef,
	);
	/* eslint-enable @atlassian/relay/unused-fields */
}

function useLocalMutation() {
	return useMutation<CustomisationMutationType>(graphql`
		mutation useNetworkSideNavUserDataMutation(
			$entityAri: ID!
			$ownerAri: ID!
			$sidebar: [SettingsMenuItemInput]
			$properties: [SettingsDisplayPropertyInput]
		) {
			settings_updateNavigationCustomisation(
				input: {
					entityAri: $entityAri
					ownerAri: $ownerAri
					properties: $properties
					sidebar: $sidebar
				}
			) {
				sidebar {
					nodes {
						menuId
						visible
					}
				}
				properties {
					nodes {
						key
						value
					}
				}
			}
		}
	`)[0];
}

type ExecuteMutationParams = {
	mutation: ReturnType<typeof useLocalMutation>;
	variables: useNetworkSideNavUserDataMutation$variables;
};

function executeMutation({ mutation, variables }: ExecuteMutationParams) {
	try {
		return mutation({
			variables: variables,
			onCompleted: () => {
				// TODO: fire analytic event for success after NAVX-490 is merged
			},
			onError: (/*error*/) => {
				// TODO: fire analytic event for success after NAVX-490 is merged
			},
		});
	} catch (error) {}
}
